import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Shield, BookOpen, Users, Mic, ArrowRight } from "lucide-react";
import { listLeadership } from "../lib/api";
import { LEADERSHIP } from "../lib/content";

const CO_DIRECTOR = LEADERSHIP.find((l) => l.role === "Co-Director");

function CoDirector() {
  const p = CO_DIRECTOR;
  return (
    <section className="bg-[#0A0A0A] text-white py-20 md:py-24 relative overflow-hidden" data-testid="co-director-section">
      <div className="noise" />
      <div className="container-x relative grid gap-12 md:grid-cols-12 items-center">
        <div className="md:col-span-4">
          {p.photo ? (
            <img src={p.photo} alt={p.name} className="w-full max-w-xs aspect-[4/5] rounded-2xl object-cover ring-1 ring-white/15" data-testid="co-director-photo" />
          ) : (
            <div className="w-full max-w-xs aspect-[4/5] rounded-2xl bg-gradient-to-br from-[#6B21A8] to-[#F5B90A]/70 flex items-center justify-center font-display text-7xl font-bold" data-testid="co-director-initials">
              WW
            </div>
          )}
        </div>
        <div className="md:col-span-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="gold-line" />
            <span className="overline text-[#F5B90A]">Leading Alongside the Founder</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold" data-testid="co-director-name">{p.name}</h2>
          <p className="mt-2 text-[#F5B90A] font-semibold" data-testid="co-director-role">{p.role}</p>
          <p className="mt-6 max-w-2xl text-white/80 leading-relaxed" data-testid="co-director-bio">{p.bio}</p>
        </div>
      </div>
    </section>
  );
}

export default function Founder() {
  const { data: leaders = [] } = useQuery({ queryKey: ["leadership"], queryFn: listLeadership });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="founder-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-24 md:py-32 grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Founder & Executive Director</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight">
              Duane Thomas Jr.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/80">
              Law enforcement professional. Community leader. Minister. Entrepreneur. Author. Youth mentor.
              Builder of futures for young men.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-gold" data-testid="founder-contact"><ArrowRight className="h-4 w-4" /> Contact Our Team</Link>
              <Link to="/partnerships" className="btn-outline-white" data-testid="founder-partner">Partnership Opportunities</Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#F5B90A]/25 via-[#6B21A8]/20 to-transparent rounded-2xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_30px_80px_-20px_rgba(107,33,168,0.6)]">
                <img
                  src="/photos/duane-thomas.jpg"
                  alt="Duane Thomas Jr., Founder & Executive Director of The Change Center Mentoring Group"
                  className="w-full h-auto block"
                  data-testid="founder-portrait"
                />
              </div>
              <p className="mt-4 text-center text-xs text-white/50 italic">Duane Thomas Jr. — Founder & Executive Director</p>
            </div>
          </div>
        </div>
      </section>

      {/* BIO */}
      <section className="bg-white py-20 md:py-24" data-testid="founder-bio">
        <div className="container-x grid gap-14 md:grid-cols-12">
          <div className="md:col-span-7">
            <span className="overline text-[#6B21A8]">The Story</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">A vision rooted in community.</h2>
            <div className="mt-8 space-y-5 text-black/80 leading-relaxed">
              <p>
                Duane Thomas Jr. is a law enforcement professional, community leader, minister, entrepreneur, author, and
                youth mentor dedicated to helping young men build stronger futures.
              </p>
              <p>
                His vision for Change Center Mentoring Group is rooted in the belief that consistent mentorship, education,
                accountability, opportunity, and positive relationships can help redirect lives and strengthen communities.
              </p>
              <p>
                Through his community leadership and professional experience, he continues working to expand resources and
                opportunities for underserved young men and their families across Clayton County and beyond.
              </p>
              <p className="text-sm text-black/60 italic">
                Change Center Mentoring Group is an independent community nonprofit, not a political campaign, police
                department program, or church ministry.
              </p>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-lg bg-[#F7F3EC] p-8">
              <p className="overline text-[#6B21A8]">Fields of Service</p>
              <div className="mt-5 space-y-4">
                {[
                  { icon: Shield, label: "Law Enforcement Professional" },
                  { icon: Users, label: "Community Leader" },
                  { icon: BookOpen, label: "Author" },
                  { icon: Mic, label: "Minister & Speaker" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#6B21A8]/10 text-[#6B21A8]">
                      <item.icon className="h-4 w-4" />
                    </div>
                    <p className="font-medium">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CoDirector />

      {/* LEADERSHIP TEAM */}
      {leaders.length > 0 && (
        <section className="bg-[#F7F3EC] py-20" data-testid="founder-team">
          <div className="container-x">
            <h2 className="font-display text-4xl md:text-5xl font-bold">Our leadership team.</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3 lg:grid-cols-4">
              {leaders.map((l) => (
                <div key={l.id} className="card-editorial p-6 bg-white" data-testid={`leader-${l.id}`}>
                  {l.photo ? (
                    <img src={l.photo} alt={l.name} className="h-24 w-24 rounded-full object-cover" />
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-gradient-to-br from-[#6B21A8] to-[#F5B90A] flex items-center justify-center text-white font-display text-3xl font-bold">
                      {l.name?.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <h4 className="mt-5 font-display text-lg font-bold">{l.name}</h4>
                  <p className="text-sm text-[#6B21A8] font-semibold">{l.role}</p>
                  {l.bio && <p className="mt-2 text-sm text-black/70">{l.bio}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-white py-20" data-testid="founder-cta">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl font-bold">Join the mission.</h2>
          <p className="mt-4 text-black/70 text-lg">Enroll a young man, step up as a mentor, or partner with us at the organizational level.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/enroll" className="btn-primary">Enroll Your Child</Link>
            <Link to="/become-a-mentor" className="btn-outline">Become a Mentor</Link>
            <Link to="/partnerships" className="btn-outline">Partner With Us</Link>
          </div>
        </div>
      </section>
    </>
  );
}
